import React, { useMemo, useRef, useState } from 'react';
import { sendMapZoneChat } from 'pages/Chat/chatApi';
import MapSpotlightChat from './MapSpotlightChat';

const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const ZONE_CHIPS = [
  'What bleaching surveys are in this zone?',
  'Summarize SST / DHW at the zone center.',
  'Which CoralFuture projects and colonies are here?',
];

const NO_ZONE_CHIPS = [
  'Select a zone to analyze bleaching, SST, and projects.',
];

export default function MapAiAssistant({
  zoneBbox,
  onClearZone,
  selectMode,
  onToggleSelect,
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const lastSentRef = useRef('');

  const suggestions = useMemo(
    () => (zoneBbox ? ZONE_CHIPS : NO_ZONE_CHIPS),
    [zoneBbox],
  );

  const send = async (raw, { echo = true } = {}) => {
    const content = String(raw || '').trim();
    if (!content || loading) return;
    if (!zoneBbox) {
      setOpen(true);
      setError('Select a map zone first (rectangle button), then ask about that region.');
      return;
    }
    lastSentRef.current = content;
    setOpen(true);
    setText('');
    setError('');
    const prior = messages.map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));
    if (echo) {
      setMessages((prev) => [
        ...prev,
        { id: makeId(), role: 'user', content },
      ]);
    }
    setLoading(true);
    try {
      const result = await sendMapZoneChat({
        content,
        bbox: zoneBbox,
        history: prior,
      });
      const assistant = result.assistantMessage || {
        role: 'assistant',
        content: result.message,
        links: result.links || [],
      };
      setMessages((prev) => {
        const base = echo
          ? prev
          : [...prev, { id: makeId(), role: 'user', content }];
        return [
          ...base,
          {
            id: makeId(),
            role: 'assistant',
            content: assistant.content || result.message || '',
            links: assistant.links || result.links || [],
          },
        ];
      });
    } catch (err) {
      setError(err.message || 'The assistant is unavailable right now.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MapSpotlightChat
      open={open}
      onOpenChange={setOpen}
      messages={messages}
      text={text}
      onTextChange={setText}
      onSend={send}
      onSuggestionSelect={(chip) => send(chip)}
      suggestions={suggestions}
      loading={loading}
      typing={loading}
      error={error}
      onRetry={() => {
        if (lastSentRef.current) send(lastSentRef.current, { echo: false });
      }}
      authenticated
      zoneBbox={zoneBbox}
      onClearZone={onClearZone}
      selectMode={selectMode}
      onToggleSelect={onToggleSelect}
      fullChatHref="/chat"
    />
  );
}
